import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ReducedMotionProvider, useReducedMotionSetting } from "@/context/reduced-motion";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { MotionConfig } from "framer-motion";
import type { ReactNode } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Suspense, lazy } from "react";
import { ScrollToTop } from "@/components/ScrollToTop";
import { AuthProvider } from "@/context/AuthContext";
import { publicRoutes } from "./routes/publicRoutes";
import { authRoutes } from "./routes/authRoutes";
import { CookieConsent } from "@/components/CookieConsent";

const queryClient = new QueryClient();

const NotFound = lazy(() => import("@/pages/NotFound"));

const RouteLoader = () => (
    <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
    </div>
);

const MotionConfigWrapper = ({ children }: { children: ReactNode }) => {
    const { reducedMotion } = useReducedMotionSetting();

    return (
        <MotionConfig reducedMotion={reducedMotion ? "always" : "never"}>
            {children}
        </MotionConfig>
    );
};

export const AppPublic = () => (
    <ReducedMotionProvider>
        <MotionConfigWrapper>
            <QueryClientProvider client={queryClient}>
                <AuthProvider>
                    <TooltipProvider>
                        <Toaster />
                        <Sonner />
                        <BrowserRouter>
                            <ScrollToTop />
                            <CookieConsent />
                            <Suspense fallback={<RouteLoader />}>
                                <Routes>
                                    <Route path="/" element={<Navigate to="/playbook" replace />} />
                                    <Route path="/tech-leadership" element={<Navigate to="/playbook" replace />} />
                                    {publicRoutes}
                                    {authRoutes}
                                    <Route path="*" element={<NotFound />} />
                                </Routes>
                            </Suspense>
                        </BrowserRouter>
                    </TooltipProvider>
                </AuthProvider>
            </QueryClientProvider>
        </MotionConfigWrapper>
    </ReducedMotionProvider>
);
